import React, { useState, useEffect, useMemo } from 'react';
import { useDataQuery, useDataEngine } from '@dhis2/app-runtime';
import { Card, CircularLoader } from '@dhis2/ui';
import { resolveActiveFiscalYear, getFallbackFiscalYear } from '../../GoalsManagement/utils/fiscalYearHelpers';

const TASK_TO_OBJECTIVE_DE = 'plJovUlxfQ0';
const POSITION_TEI_DE = 'SlgO60cMK3F';
const REPORTING_TO_ATTR = 'SjP47qi3JeB';
const POSITION_TE_TYPE = 'UGGo0Ebjl0E';
const APPROVAL_STATUS_DE = 'DL7KxNhiV0x';

// Fiscal year program config
const FISCAL_YEAR_CONFIG = {
  program: "NkReudUAW4F",
  stage: "htIWgNQWEMm",
  dataElements: {
    start: "U55MqwBcRAe",
    end: "kgdk4NXu4f5"
  }
};

const ReviewReminders = ({ orgUnitId, performanceData, currentFilter = 'all', selectedFiscalYear = null }) => {
  const engine = useDataEngine();


  // State for processed (summary) data
  const [companyGoalsData, setCompanyGoalsData] = useState({
    totalGoals: 0,
    goalsWithoutObjectives: 0,
    activeGoalsEndDate: null,
    daysUntilNewGoalsCreation: null,
    canCreateNewGoals: false
  });

  const [objectivesData, setObjectivesData] = useState({
    pendingApproval: 0,
    returnedForReview: 0,
    objectivesWithoutTasks: 0,
    objectivesWithoutRecentTasks: 0,
    totalObjectives: 0
  });

  const [kpiSummaryData, setKpiSummaryData] = useState({
    totalKPIs: 0,
    avgProgress: 0,
    onTrackKPIs: 0,
    completedKPIs: 0,
    behindKPIs: 0,
    completionRate: 0
  });

  const [leadershipReminders, setLeadershipReminders] = useState({
    weightsDeadline: null,
    daysUntilWeights: null,
    midYearDeadline: null,
    daysUntilMidYear: null,
    yearEndDeadline: null,
    daysUntilYearEnd: null,
    currentPhase: 'weights',
    fiscalYearStart: null,
    fiscalYearEnd: null,
    isLoading: true
  });

  // ─────────────────────────────────────────────────────────────────────
  // FIXED: `allGoals`, `allObjectives`, `allTasks`, `allKpis` no longer
  // reference `goalsData`, `objectivesDataRaw`, `tasksData`, `kpisData`.
  // They read ONLY from `performanceData` (the prop).
  // ─────────────────────────────────────────────────────────────────────
  const allGoals = useMemo(
    () => performanceData?.goals?.events || [],
    [performanceData]
  );
  const allObjectives = useMemo(
    () => performanceData?.objectives?.events || [],
    [performanceData]
  );
  const allTasks = useMemo(
    () => performanceData?.tasks?.events || [],
    [performanceData]
  );
  const allKpis = useMemo(
    () => performanceData?.kpis?.events || [],
    [performanceData]
  );

  // ===== FISCAL YEAR FETCHING =====
  useEffect(() => {
    const loadFiscalYear = async () => {
      if (!orgUnitId || !engine) {
        setLeadershipReminders(prev => ({ ...prev, isLoading: false }));
        return;
      }

      try {
        console.log('🔍 [ReviewReminders] Loading fiscal year for orgUnit:', orgUnitId);

        const enrollmentsResponse = await engine.query({
          enrollmentsQuery: {
            resource: 'enrollments',
            params: {
              ouMode: 'ACCESSIBLE',
              program: FISCAL_YEAR_CONFIG.program,
              fields: 'trackedEntityInstance',
              pageSize: 1,
              order: 'lastUpdated:desc'
            }
          }
        });

        const teiId = enrollmentsResponse.enrollmentsQuery?.enrollments?.[0]?.trackedEntityInstance;
        if (!teiId) {
          console.warn('⚠️ [ReviewReminders] No Staff TEI found for fiscal year load');
          setLeadershipReminders(prev => ({ ...prev, isLoading: false }));
          return;
        }

        console.log('📋 [ReviewReminders] Found TEI for fiscal year:', teiId);

        const eventsResponse = await engine.query({
          eventsQuery: {
            resource: 'events',
            params: {
              program: FISCAL_YEAR_CONFIG.program,
              programStage: FISCAL_YEAR_CONFIG.stage,
              ouMode: 'ACCESSIBLE',
              trackedEntityInstance: teiId,
              pageSize: 1,
              order: 'eventDate:desc',
              fields: 'dataValues[dataElement,value]'
            }
          }
        });

        const event = eventsResponse.eventsQuery?.events?.[0];
        let start = null, end = null;

        if (event) {
          event.dataValues?.forEach(dv => {
            if (dv.dataElement === FISCAL_YEAR_CONFIG.dataElements.start) start = dv.value;
            if (dv.dataElement === FISCAL_YEAR_CONFIG.dataElements.end) end = dv.value;
          });
        }

        let resolvedFy;
        if (start && end) {
          resolvedFy = resolveActiveFiscalYear({ start, end });
        } else {
          resolvedFy = getFallbackFiscalYear();
        }

        const now = new Date();
        const startDate = new Date(resolvedFy.start);
        const yearEndDeadline = new Date(resolvedFy.end);

        const weightsDeadline = new Date(startDate);
        weightsDeadline.setMonth(weightsDeadline.getMonth() + 1);

        const midYearDeadline = new Date(startDate);
        midYearDeadline.setMonth(midYearDeadline.getMonth() + 6);

        let currentPhase = 'weights';
        if (now > weightsDeadline && now <= midYearDeadline) {
          currentPhase = 'midyear';
        } else if (now > midYearDeadline) {
          currentPhase = 'yearend';
        }

        const daysUntilWeights = calculateDaysUntil(weightsDeadline);
        const daysUntilMidYear = calculateDaysUntil(midYearDeadline);
        const daysUntilYearEnd = calculateDaysUntil(yearEndDeadline);

        setLeadershipReminders({
          weightsDeadline,
          daysUntilWeights,
          midYearDeadline,
          daysUntilMidYear,
          yearEndDeadline,
          daysUntilYearEnd,
          currentPhase,
          fiscalYearStart: startDate,
          fiscalYearEnd: yearEndDeadline,
          isLoading: false
        });

        console.log('📅 [ReviewReminders] Fiscal year loaded:', {
          start: startDate.toISOString(),
          end: yearEndDeadline.toISOString()
        });

      } catch (error) {
        console.error('❌ [ReviewReminders] Failed to load fiscal year:', error);
        const startDate = new Date();
        startDate.setMonth(6);
        startDate.setDate(1);
        startDate.setHours(0, 0, 0, 0);

        setLeadershipReminders({
          weightsDeadline: new Date(startDate),
          daysUntilWeights: 30,
          midYearDeadline: new Date(startDate),
          daysUntilMidYear: 180,
          yearEndDeadline: new Date(startDate),
          daysUntilYearEnd: 365,
          currentPhase: 'weights',
          fiscalYearStart: startDate,
          fiscalYearEnd: new Date(startDate),
          isLoading: false
        });
      }
    };

    loadFiscalYear();
  }, [orgUnitId, engine]);

  // Helper function to get date from item
  const getItemDate = (item) => {
    let date = item?.eventDate || item?.occurredAt || item?.createdAt || item?.dueDate;
    if (date) {
      try {
        const d = new Date(date);
        if (!isNaN(d.getTime())) return date;
      } catch (e) { /* ignore */ }
    }

    const dataValues = item?.dataValues || [];
    const dateFields = [
      'MH7DQVegD1j', 'pUvJQ9vy3A3',
      'eDcVy661j9e', 'J0rjxrDKn1l',
      'EiL3lPaL908',
      'U55MqwBcRAe', 'kgdk4NXu4f5'
    ];

    for (const de of dateFields) {
      const found = dataValues.find(dv => dv.dataElement === de)?.value;
      if (found) {
        try {
          const d = new Date(found);
          if (!isNaN(d.getTime())) return found;
        } catch (e) { /* ignore */ }
      }
    }

    return null;
  };

  // ─────────────────────────────────────────────────────────────────────
  // complete the filterFn body. Your uploaded file had a stub
  // `filterFn = (items) => items.filter(item => { /* ...same body... */ });`
  // which is a syntax error waiting to happen. Replaced with the real body.
  // ─────────────────────────────────────────────────────────────────────
  const { filteredGoals, filteredObjectives, filteredTasks, filteredKpis } = useMemo(() => {
    const fiscalYearToUse = currentFilter === 'all'
      ? null
      : selectedFiscalYear?.start && selectedFiscalYear?.end
        ? selectedFiscalYear
        : (leadershipReminders.fiscalYearStart && leadershipReminders.fiscalYearEnd)
          ? { start: leadershipReminders.fiscalYearStart, end: leadershipReminders.fiscalYearEnd }
          : null;

    const fyStart = fiscalYearToUse?.start;
    const fyEnd = fiscalYearToUse?.end;

    if (!fyStart || !fyEnd) {
      return {
        filteredGoals: allGoals,
        filteredObjectives: allObjectives,
        filteredTasks: allTasks,
        filteredKpis: allKpis,
      };
    }

    const fiscalStart = new Date(fyStart); fiscalStart.setHours(0, 0, 0, 0);
    const fiscalEnd   = new Date(fyEnd);   fiscalEnd.setHours(23, 59, 59, 999);

    const filterFn = (items) => items.filter(item => {
      const dateValue = getItemDate(item);
      if (!dateValue) return true; // include items with no date

      try {
        const itemDate = new Date(dateValue);
        if (isNaN(itemDate.getTime())) return true;

        const normalizedItemDate = new Date(itemDate);
        normalizedItemDate.setHours(0, 0, 0, 0);

        const isInRange = normalizedItemDate >= fiscalStart && normalizedItemDate <= fiscalEnd;

        // If item is a goal with both start and end dates, check both
        if (item.dataValues) {
          const startDV = item.dataValues.find(dv => dv.dataElement === 'MH7DQVegD1j');
          const endDV = item.dataValues.find(dv => dv.dataElement === 'pUvJQ9vy3A3');

          if (startDV?.value && endDV?.value) {
            const goalStart = new Date(startDV.value);
            const goalEnd = new Date(endDV.value);
            if (!isNaN(goalStart.getTime()) && !isNaN(goalEnd.getTime())) {
              goalStart.setHours(0, 0, 0, 0);
              goalEnd.setHours(23, 59, 59, 999);
              return goalStart <= fiscalEnd && goalEnd >= fiscalStart;
            }
          }
        }

        return isInRange;
      } catch (e) {
        return true;
      }
    });

    return {
      filteredGoals: filterFn(allGoals),
      filteredObjectives: filterFn(allObjectives),
      filteredTasks: filterFn(allTasks),
      filteredKpis: filterFn(allKpis),
    };
  }, [
    allGoals, allObjectives, allTasks, allKpis,
    currentFilter, selectedFiscalYear,
    leadershipReminders.fiscalYearStart,
    leadershipReminders.fiscalYearEnd,
  ]);

  // Position mapping
  const positionTEIs = useMemo(() => Array.from(
    new Set(
      filteredObjectives
        .map(obj => obj.dataValues?.find(dv => dv.dataElement === POSITION_TEI_DE)?.value)
        .filter(Boolean)
    )
  ).join(';'), [filteredObjectives]);

  const positionsQuery = useMemo(() => ({
    positions: {
      resource: 'trackedEntityInstances',
      params: {
        trackedEntityType: POSITION_TE_TYPE,
        ou: orgUnitId,
        ouMode: 'DESCENDANTS',
        fields: 'trackedEntityInstance,attributes[attribute,value]',
        pageSize: 1000
      }
    }
  }), [orgUnitId]);

  const { loading: positionsLoading, data: positionsData } = useDataQuery(positionsQuery);

  // Helper functions
  const calculateDaysUntil = (targetDate) => {
    try {
      const now = new Date();
      const target = new Date(targetDate);
      if (isNaN(now.getTime()) || isNaN(target.getTime())) return 0;
      const diffTime = target - now;
      return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    } catch (error) {
      return 0;
    }
  };

  const getTaskProgressFromSubtasks = (taskDataValues) => {
    if (!taskDataValues) return 0;
    const subTasksJson = taskDataValues.find(dv => dv.dataElement === 'YBXgnhyue59')?.value;
    let subTasks = [];
    try {
      subTasks = subTasksJson ? JSON.parse(subTasksJson) : [];
    } catch (e) {
      subTasks = [];
    }
    if (subTasks.length > 0) {
      const totalProgress = subTasks.reduce((sum, st) => {
        const progress = typeof st.progressPercent === 'string' ? parseFloat(st.progressPercent) : st.progressPercent;
        return sum + (Number.isFinite(progress) ? progress : 0);
      }, 0);
      return Math.round(totalProgress / subTasks.length);
    }
    const topLevelProgress = taskDataValues.find(dv => dv.dataElement === 'AElpNxzTdRh')?.value;
    if (topLevelProgress !== undefined && topLevelProgress !== null) {
      return parseInt(topLevelProgress, 10) || 0;
    }
    return 0;
  };

  const calculateKPIProgress = (kpi, tasks) => {
    if (!tasks || tasks.length === 0) return 0;
    const kpiDataValues = kpi.dataValues || [];
    const objectiveId = kpiDataValues.find(dv => dv.dataElement === 'plJovUlxfQ0')?.value;
    if (!objectiveId) return 0;
    const objectiveTasks = tasks.filter(task => {
      const taskDataValues = task.dataValues || [];
      const taskObjectiveId = taskDataValues.find(dv => dv.dataElement === 'plJovUlxfQ0')?.value;
      return taskObjectiveId === objectiveId;
    });
    if (objectiveTasks.length === 0) return 0;
    const taskProgresses = objectiveTasks.map(task => {
      const taskProgress = getTaskProgressFromSubtasks(task.dataValues);
      const taskDataValues = task.dataValues || [];
      const status = taskDataValues.find(dv => dv.dataElement === 'LuTwfuN34pc')?.value;
      const isCompleted = status === 'completed' || status === 'Completed';
      return isCompleted ? 100 : taskProgress;
    });
    const avgProgress = taskProgresses.reduce((sum, p) => sum + p, 0) / taskProgresses.length;
    const completedTasks = taskProgresses.filter(p => p >= 95).length;
    const completionRate = (completedTasks / taskProgresses.length) * 100;
    const weightedProgress = (completionRate * 0.7) + (avgProgress * 0.3);
    return Math.round(Math.min(weightedProgress, 100));
  };

  // ===== PROCESS COMPANY GOALS DATA =====
  // ─────────────────────────────────────────────────────────────────────
  // goalsWithoutObjectives now checks the actual VhrIQQIVpB9
  // linkage instead of "any objective in the same org unit".
  // ─────────────────────────────────────────────────────────────────────
  useEffect(() => {
    const goals = filteredGoals;
    const objectives = filteredObjectives;
    const totalGoals = goals.length;

    // Build set of goal IDs that ARE linked from some objective
    const linkedGoalIds = new Set();
    objectives.forEach(obj => {
      const gid = obj.dataValues?.find(dv => dv.dataElement === 'VhrIQQIVpB9')?.value;
      if (gid) linkedGoalIds.add(gid);
    });

    let goalsWithoutObjectives = 0;
    goals.forEach(goal => {
      if (!linkedGoalIds.has(goal.event)) {
        goalsWithoutObjectives++;
      }
    });

    const goalsWithEndDates = goals.filter(goal => {
      const dataValues = goal.dataValues || [];
      const endDate = dataValues.find(dv =>
        dv.dataElement === 'J0rjxrDKn1l' ||
        dv.dataElement === 'pUvJQ9vy3A3' ||
        dv.dataElement === 'dueDate'
      )?.value;
      return endDate;
    });

    const sortedGoals = [...goalsWithEndDates].sort((a, b) => {
      const getGoalDate = (goal) => {
        const dataValues = goal.dataValues || [];
        return dataValues.find(dv =>
          dv.dataElement === 'J0rjxrDKn1l' ||
          dv.dataElement === 'pUvJQ9vy3A3' ||
          dv.dataElement === 'dueDate'
        )?.value;
      };
      const aDate = getGoalDate(a);
      const bDate = getGoalDate(b);
      return new Date(bDate) - new Date(aDate);
    });

    const activeGoalsEndDate = sortedGoals[0] ?
      sortedGoals[0].dataValues.find(dv =>
        dv.dataElement === 'J0rjxrDKn1l' ||
        dv.dataElement === 'pUvJQ9vy3A3' ||
        dv.dataElement === 'dueDate'
      )?.value : null;

    let daysUntilNewGoalsCreation = null;
    let canCreateNewGoals = false;

    if (activeGoalsEndDate) {
      const endDate = new Date(activeGoalsEndDate);
      const newGoalsDate = new Date(endDate);
      newGoalsDate.setDate(newGoalsDate.getDate() + 30);
      daysUntilNewGoalsCreation = calculateDaysUntil(newGoalsDate);
      canCreateNewGoals = daysUntilNewGoalsCreation <= 0;
    } else if (totalGoals === 0) {
      canCreateNewGoals = true;
      daysUntilNewGoalsCreation = 0;
    }

    setCompanyGoalsData({
      totalGoals,
      goalsWithoutObjectives,
      activeGoalsEndDate,
      daysUntilNewGoalsCreation,
      canCreateNewGoals
    });
  }, [filteredGoals, filteredObjectives]);

  // ===== PROCESS OBJECTIVES DATA =====
  useEffect(() => {
    if (!filteredObjectives.length) {
      setObjectivesData({
        pendingApproval: 0,
        returnedForReview: 0,
        objectivesWithoutTasks: 0,
        objectivesWithoutRecentTasks: 0,
        totalObjectives: 0
      });
      return;
    }

    const positionMap = new Map();
    if (positionsData?.positions?.trackedEntityInstances) {
      positionsData.positions.trackedEntityInstances.forEach(pos => {
        const attrs = new Map(
          (pos.attributes || []).map(attr => [attr.attribute, attr.value])
        );
        const hasReportingTo = attrs.has(REPORTING_TO_ATTR) && attrs.get(REPORTING_TO_ATTR);
        positionMap.set(pos.trackedEntityInstance, !!hasReportingTo);
      });
    }

    const objectives = filteredObjectives;
    const totalObjectives = objectives.length;

    let pendingApproval = 0;
    let returnedForReview = 0;

    objectives.forEach(obj => {
      const dataValues = obj.dataValues || [];
      const approvalStatus = dataValues.find(dv => dv.dataElement === APPROVAL_STATUS_DE)?.value;
      const positionTEI = dataValues.find(dv => dv.dataElement === POSITION_TEI_DE)?.value;

      const isTopLevel = positionTEI ? !positionMap.get(positionTEI) : false;

      console.log(`📊 Objective ${obj.event}:`, {
        approvalStatus,
        positionTEI,
        isTopLevel,
        positionMapHas: positionTEI ? positionMap.has(positionTEI) : false,
        reportingToValue: positionTEI ? positionMap.get(positionTEI) : 'N/A'
      });

      if (isTopLevel) return;

      const statusLower = (approvalStatus || '').toLowerCase();
      const isPending = statusLower === 'pending' ||
                         statusLower === 'submitted' ||
                         statusLower === 'draft' ||
                         statusLower === 'in progress' ||
                         !approvalStatus;

      if (isPending) {
        pendingApproval++;
      } else if (['returned', 'rejected', 'needs revision'].includes(statusLower)) {
        returnedForReview++;
      }
    });

    console.log('📊 ReviewReminders - Pending Approval Count:', pendingApproval);

    const objectivesWithTasks = new Set();
    const objectivesWithRecentTasks = new Set();
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    sevenDaysAgo.setHours(0, 0, 0, 0);

    if (filteredTasks.length) {
      filteredTasks.forEach(task => {
        const dataValues = task.dataValues || [];
        const objectiveId = dataValues.find(dv => dv.dataElement === TASK_TO_OBJECTIVE_DE)?.value;
        if (objectiveId) {
          objectivesWithTasks.add(objectiveId);
          const selectedWeekValue = dataValues.find(dv => dv.dataElement === 'EiL3lPaL908')?.value;
          if (selectedWeekValue) {
            const taskWeekDate = new Date(selectedWeekValue);
            taskWeekDate.setHours(0, 0, 0, 0);
            if (taskWeekDate >= sevenDaysAgo) {
              objectivesWithRecentTasks.add(objectiveId);
            }
          }
        }
      });
    }

    const objectivesWithoutTasks = objectives.filter(obj =>
      !objectivesWithTasks.has(obj.event)
    ).length;

    const objectivesWithoutRecentTasks = objectives.filter(obj =>
      !objectivesWithRecentTasks.has(obj.event)
    ).length;

    setObjectivesData({
      pendingApproval,
      returnedForReview,
      objectivesWithoutTasks,
      objectivesWithoutRecentTasks,
      totalObjectives
    });
  }, [filteredObjectives, filteredTasks, positionsData]);

  // ===== PROCESS KPI DATA =====
  // ─────────────────────────────────────────────────────────────────────
  // FIXED: removed `kpiDataReady` gate. Because filteredKpis and
  // filteredObjectives are now derived via useMemo, they are already
  // populated when this effect runs on the correct render.
  // ─────────────────────────────────────────────────────────────────────
  useEffect(() => {
    const allKpisList = filteredKpis || [];
    const objectives = filteredObjectives || [];

    const validObjectiveIds = new Set();
    objectives.forEach(obj => {
      const id = obj?.id || obj?.event || obj?.eventId || obj?.objectiveId;
      if (id) validObjectiveIds.add(id);
    });

    console.log('📊 ReviewReminders - Processing KPIs:', {
      totalKpis: allKpisList.length,
      totalObjectives: objectives.length,
      objectiveIds: Array.from(validObjectiveIds)
    });

    if (validObjectiveIds.size === 0) {
      setKpiSummaryData({
        totalKPIs: 0,
        avgProgress: 0,
        onTrackKPIs: 0,
        completedKPIs: 0,
        behindKPIs: 0,
        completionRate: 0
      });
      return;
    }

    const displayableKpis = allKpisList.filter(kpi => {
      const dataValues = kpi.dataValues || [];
      const name = dataValues.find(dv => dv.dataElement === 'DBmylDQA9KX')?.value;
      if (!name || name.trim() === '') return false;

      const objectiveLink = dataValues.find(dv =>
        dv.dataElement === 'plJovUlxfQ0' ||
        dv.dataElement === 'neuilg3L4d2'
      )?.value;

      if (objectiveLink) return validObjectiveIds.has(objectiveLink);
      return false;
    });

    console.log('📊 ReviewReminders - Displayable KPIs:', displayableKpis.length);

    if (!displayableKpis.length) {
      setKpiSummaryData({
        totalKPIs: 0,
        avgProgress: 0,
        onTrackKPIs: 0,
        completedKPIs: 0,
        behindKPIs: 0,
        completionRate: 0
      });
      return;
    }

    const tasks = filteredTasks;
    const totalKPIs = displayableKpis.length;

    const kpisWithProgress = displayableKpis.map(kpi => ({
      ...kpi,
      progress: calculateKPIProgress(kpi, tasks)
    }));

    const totalProgress = kpisWithProgress.reduce((sum, kpi) => sum + kpi.progress, 0);
    const avgProgress = totalKPIs > 0 ? Math.round(totalProgress / totalKPIs) : 0;
    const onTrackKPIs = kpisWithProgress.filter(kpi => kpi.progress >= 70).length;
    const completedKPIs = kpisWithProgress.filter(kpi => kpi.progress >= 95).length;
    const behindKPIs = kpisWithProgress.filter(kpi => kpi.progress < 50).length;
    const completionRate = totalKPIs > 0 ? Math.round((completedKPIs / totalKPIs) * 100) : 0;

    setKpiSummaryData({
      totalKPIs,
      avgProgress,
      onTrackKPIs,
      completedKPIs,
      behindKPIs,
      completionRate
    });
  }, [filteredKpis, filteredTasks, filteredObjectives]);

  // ─────────────────────────────────────────────────────────────────────
  // FIXED: isLoading/error no longer reference the removed variables.
  // ─────────────────────────────────────────────────────────────────────
  const isLoading = leadershipReminders.isLoading || positionsLoading;

  // ===== FORMATTING FUNCTIONS =====
  const formatDaysDisplay = (days) => {
    if (days === 0) return 'Today';
    if (days === 1) return 'Tomorrow';
    if (days < 0) return `${Math.abs(days)} days ago`;
    if (days < 7) return `${days} days`;
    if (days < 30) return `${Math.ceil(days / 7)} weeks`;
    return `${Math.ceil(days / 30)} months`;
  };

  const formatDateDisplay = (date) => {
    if (!date) return 'Not set';
    try {
      const dateObj = new Date(date);
      if (isNaN(dateObj.getTime())) return 'Invalid date';
      return dateObj.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch (error) {
      return 'Invalid date';
    }
  };

  const getProgressColor = (value) => {
    if (value >= 80) return '#28a745';
    if (value >= 50) return '#ffc107';
    return '#dc3545';
  };

  // ===== RENDER =====
  if (isLoading) {
    return (
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
        gap: 20,
        marginBottom: 20,
        width: '100%'
      }}>
        {[1, 2, 3, 4, 5].map(i => (
          <Card key={i} style={{ padding: 16 }}>
            <h3>Loading...</h3>
            <CircularLoader small />
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
      gap: 20,
      marginBottom: 20,
      width: '100%'
    }}>
      {/* Card 1: Company Goals Overview */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        boxShadow: "0 8px 10px rgba(240, 229, 203, 1)",
        borderRadius: '12px',
        minHeight: '220px',
      }}>
        <h3 style={{
          margin: 0,
          fontSize: '1rem',
          color: '#c18c06ff',
          marginBottom: '10px',
          padding: '16px 16px 0 16px'
        }}>
          Company Goals Overview
        </h3>
        <div style={{
          flex: 1,
          padding: '16px',
          border: '1px solid #e9ecef',
          borderRadius: '8px',
          backgroundColor: 'white'
        }}>
          <div style={{
            padding: '16px',
            border: '1px solid #fff',
            borderRadius: '16px',
            backgroundColor: '#fff3cd',
            height: '100%',
            boxSizing: 'border-box'
          }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '12px' }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#856404' }}>
                  {companyGoalsData.totalGoals}
                </div>
                <div style={{ fontSize: '10px', color: '#856404' }}>Total Goals</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{
                  fontSize: '24px',
                  fontWeight: 'bold',
                  color: companyGoalsData.goalsWithoutObjectives > 0 ? '#dc3545' : '#28a745'
                }}>
                  {companyGoalsData.goalsWithoutObjectives}
                </div>
                <div style={{ fontSize: '10px', color: '#856404' }}>Without Objectives</div>
              </div>
            </div>

            {companyGoalsData.activeGoalsEndDate && (
              <div style={{
                fontSize: '12px',
                color: '#856404',
                marginBottom: '8px',
                padding: '8px',
                backgroundColor: 'rgba(255,255,255,0.7)',
                borderRadius: '6px',
                textAlign: 'center'
              }}>
                <strong>Current Goals End:</strong><br />
                {formatDateDisplay(companyGoalsData.activeGoalsEndDate)}
              </div>
            )}

            {companyGoalsData.daysUntilNewGoalsCreation !== null && (
              <div style={{
                fontSize: '12px',
                color: companyGoalsData.canCreateNewGoals ? '#28a745' : '#856404',
                marginBottom: '8px',
                padding: '8px',
                backgroundColor: companyGoalsData.canCreateNewGoals ? '#d4edda' : 'rgba(255,255,255,0.7)',
                borderRadius: '6px',
                fontWeight: companyGoalsData.canCreateNewGoals ? '600' : 'normal',
                textAlign: 'center'
              }}>
                {companyGoalsData.canCreateNewGoals ? (
                  '✅ Ready to create new goals!'
                ) : (
                  `⏰ New goals in: ${formatDaysDisplay(companyGoalsData.daysUntilNewGoalsCreation)}`
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Card 2: Objectives Management */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        boxShadow: "0 8px 10px rgba(190, 248, 229, 1)",
        borderRadius: '12px',
        minHeight: '220px',
      }}>
        <h3 style={{
          margin: 0,
          fontSize: '1rem',
          color: '#069362ff',
          marginBottom: '10px',
          padding: '16px 16px 0 16px'
        }}>
          Objectives Overview
        </h3>
        <div style={{
          flex: 1,
          padding: '16px',
          border: '1px solid #e9ecef',
          borderRadius: '8px',
          backgroundColor: 'white'
        }}>
          <div style={{
            padding: '16px',
            border: '1px solid #fff',
            borderRadius: '16px',
            backgroundColor: '#d4f7e6ff',
            height: '100%',
            boxSizing: 'border-box'
          }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '12px' }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '20px', fontWeight: 'bold', color: objectivesData.pendingApproval > 0 ? '#dc3545' : '#28a745' }}>
                  {objectivesData.pendingApproval}
                </div>
                <div style={{ fontSize: '9px', color: '#6c757d' }}>Pending Approval</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '20px', fontWeight: 'bold', color: objectivesData.returnedForReview > 0 ? '#ffc107' : '#28a745' }}>
                  {objectivesData.returnedForReview}
                </div>
                <div style={{ fontSize: '9px', color: '#6c757d' }}>Returned for Review</div>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '12px' }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '18px', fontWeight: 'bold', color: objectivesData.objectivesWithoutTasks > 0 ? '#dc3545' : '#28a745' }}>
                  {objectivesData.objectivesWithoutTasks}
                </div>
                <div style={{ fontSize: '9px', color: '#6c757d' }}>Without Tasks</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '18px', fontWeight: 'bold', color: objectivesData.objectivesWithoutRecentTasks > 0 ? '#ffc107' : '#28a745' }}>
                  {objectivesData.objectivesWithoutRecentTasks}
                </div>
                <div style={{ fontSize: '9px', color: '#6c757d' }}>Without Recent Tasks</div>
              </div>
            </div>

            <div style={{
              fontSize: '11px',
              color: '#6c757d',
              padding: '6px',
              backgroundColor: 'rgba(255,255,255,0.7)',
              borderRadius: '4px',
              textAlign: 'center'
            }}>
              Total: {objectivesData.totalObjectives} Objectives
            </div>
          </div>
        </div>
      </div>

      {/* Card 3: KPI Performance Summary */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        boxShadow: "0 8px 10px rgba(173, 216, 230, 1)",
        borderRadius: '12px',
        minHeight: '220px',
      }}>
        <h3 style={{
          margin: 0,
          fontSize: '1rem',
          color: '#1e6ea7',
          marginBottom: '10px',
          padding: '16px 16px 0 16px'
        }}>
          KPIs Summary
        </h3>
        <div style={{
          flex: 1,
          padding: '16px',
          border: '1px solid #e9ecef',
          borderRadius: '8px',
          backgroundColor: 'white'
        }}>
          <div style={{
            padding: '16px',
            border: '1px solid #fff',
            borderRadius: '16px',
            backgroundColor: '#e3f2fd',
            height: '100%',
            boxSizing: 'border-box'
          }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '12px' }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#1e6ea7' }}>
                  {kpiSummaryData.totalKPIs}
                </div>
                <div style={{ fontSize: '9px', color: '#1e6ea7' }}>Total KPIs</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{
                  fontSize: '20px',
                  fontWeight: 'bold',
                  color: getProgressColor(kpiSummaryData.avgProgress)
                }}>
                  {kpiSummaryData.avgProgress}%
                </div>
                <div style={{ fontSize: '9px', color: '#1e6ea7' }}>Avg Progress</div>
              </div>
            </div>

            <div style={{ textAlign: 'center', marginBottom: '12px' }}>
              <div style={{
                fontSize: '18px',
                fontWeight: 'bold',
                color: kpiSummaryData.onTrackKPIs > 0 ? '#28a745' : '#6c757d'
              }}>
                {kpiSummaryData.onTrackKPIs}
              </div>
              <div style={{ fontSize: '9px', color: '#1e6ea7' }}>On Track KPIs</div>
            </div>

            <div style={{
              marginTop: '8px',
              padding: '6px',
              backgroundColor: 'rgba(255,255,255,0.7)',
              borderRadius: '4px'
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: '10px',
                color: '#1e6ea7',
                marginBottom: '2px'
              }}>
                <span>Overall Progress</span>
                <span>{kpiSummaryData.avgProgress}%</span>
              </div>
              <div style={{
                width: '100%',
                height: '6px',
                backgroundColor: '#e9ecef',
                borderRadius: '3px',
                overflow: 'hidden'
              }}>
                <div style={{
                  width: `${kpiSummaryData.avgProgress}%`,
                  height: '100%',
                  backgroundColor: getProgressColor(kpiSummaryData.avgProgress),
                  transition: 'width 0.3s ease'
                }} />
              </div>
            </div>

            <div style={{
              marginTop: '8px',
              fontSize: '10px',
              color: '#1e6ea7',
              textAlign: 'center',
              padding: '4px',
              backgroundColor: 'rgba(255,255,255,0.5)',
              borderRadius: '4px'
            }}>
              {kpiSummaryData.completedKPIs} completed • {kpiSummaryData.behindKPIs} behind
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReviewReminders;